import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { APP } from "@/constants/testIds";
import { Copy, Plus, Trash2, Users } from "lucide-react";

export default function OrgAdmin() {
  const [dashboard, setDashboard] = useState(null);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", max_uses: 100, role: "entrepreneur" });

  const load = async () => {
    try {
      const [d, e, c] = await Promise.all([
        api.get("/org/dashboard"),
        api.get("/org/entrepreneurs"),
        api.get("/org/invitation-codes"),
      ]);
      setDashboard(d.data);
      setEntrepreneurs(e.data.entrepreneurs || []);
      setCodes(c.data.codes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const createCode = async () => {
    try {
      await api.post("/org/invitation-codes", newCode);
      toast.success("Invitation code created");
      setNewCode({ code: "", max_uses: 100, role: "entrepreneur" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const deactivate = async (id) => {
    await api.delete(`/org/invitation-codes/${id}`);
    load();
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const kpis = dashboard?.kpis || {};

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">Organization Dashboard</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">{dashboard?.organization?.name || "Organization"}</h1>
        <p className="mt-2 text-sm text-stone-600">Track program impact, manage entrepreneurs, and issue invitation codes.</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {[
          ["Entrepreneurs", kpis.entrepreneurs || 0, "enrolled"],
          ["Business Plans", kpis.business_plans_generated || 0, "generated"],
          ["Health Reports", kpis.health_reports || 0, "produced"],
          ["Pitch Outlines", kpis.pitch_outlines || 0, "created"],
          ["Avg Health Score", kpis.avg_health_score || 0, "out of 100"],
        ].map(([l, v, s]) => (
          <div key={l} className="card-flat">
            <div className="label-mono">{l}</div>
            <div className="kpi-num mt-2">{v}</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-stone-500">{s}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {[
          ["Businesses Launched", kpis.businesses_launched || 0],
          ["Capital Raised", `$${(kpis.capital_raised || 0).toLocaleString()}`],
          ["Jobs Created", kpis.jobs_created || 0],
          ["AI Conversations", kpis.ai_conversations || 0],
          ["Total Assets", kpis.total_assets || 0],
        ].map(([l, v]) => (
          <div key={l} className="card-flat">
            <div className="label-mono">{l}</div>
            <div className="mt-2 font-mono text-2xl font-medium text-slate-950">{v}</div>
          </div>
        ))}
      </div>

      {/* Invitation Codes */}
      <div className="card-flat">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono">Invitation Codes</div>
            <h2 className="mt-1 font-display text-xl font-medium text-slate-950">Grant free entrepreneur access</h2>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <input value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })} placeholder="Custom code (optional)" className={`${inputCls} font-mono uppercase`} />
          <input type="number" min={1} value={newCode.max_uses} onChange={(e) => setNewCode({ ...newCode, max_uses: Number(e.target.value) })} className={inputCls} placeholder="Max uses" />
          <select value={newCode.role} onChange={(e) => setNewCode({ ...newCode, role: e.target.value })} className={inputCls}>
            <option value="entrepreneur">Entrepreneur</option>
            <option value="coach">Coach</option>
            <option value="judge">Judge</option>
            <option value="mentor">Mentor</option>
          </select>
          <button data-testid={APP.createInviteCode} onClick={createCode} className="btn-primary !text-xs"><Plus className="h-4 w-4" /> Create Code</button>
        </div>
        <div className="mt-6 rounded-md border border-stone-200 divide-y divide-stone-100 overflow-hidden">
          {codes.length === 0 && <div className="p-4 text-xs text-stone-500">No codes created yet.</div>}
          {codes.map((c) => (
            <div key={c.id} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-4">
                <button onClick={() => copy(c.code)} className="rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 font-mono text-sm font-medium text-slate-950 hover:bg-white flex items-center gap-2">
                  {c.code} <Copy className="h-3 w-3" />
                </button>
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
                  {c.uses}/{c.max_uses} used • {c.role} {c.active ? "" : "• DEACTIVATED"}
                </div>
              </div>
              {c.active && (
                <button onClick={() => deactivate(c.id)} className="btn-ghost !text-xs">
                  <Trash2 className="h-3.5 w-3.5" /> Deactivate
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Entrepreneurs */}
      <div className="card-flat">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono">Entrepreneurs ({entrepreneurs.length})</div>
            <h2 className="mt-1 font-display text-xl font-medium text-slate-950">Program Roster</h2>
          </div>
          <Users className="h-6 w-6 text-slate-950" strokeWidth={1.5} />
        </div>
        <div className="mt-6 overflow-x-auto rounded-md border border-stone-200">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left">
              <tr className="border-b border-stone-200">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Name</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Email</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Health Score</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Assets</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {entrepreneurs.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-3 font-medium text-slate-950">{e.full_name}</td>
                  <td className="px-4 py-3 text-stone-600">{e.email}</td>
                  <td className="px-4 py-3 font-mono text-slate-950">{e.latest_health ? Math.round(e.latest_health) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-stone-600">{e.assets_count}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{new Date(e.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {entrepreneurs.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-xs text-stone-500">No entrepreneurs yet. Share an invitation code to onboard your cohort.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
